/* ============================================================
   PDF export — CSS @media print only.

   Audit A-01: the scaffold loaded html2pdf from cdnjs with a literal
   `integrity="sha384-..."` placeholder. A malformed integrity value
   makes the browser reject the resource outright, so that fallback
   could never have run. It also broke the offline guarantee and would
   be blocked by the CSP in netlify.toml.

   It is gone. Native print gives selectable text, real pagination and
   correct fonts, at zero bytes. The trade is that the user goes through
   the browser's print dialog — which is a fair trade for a document
   headed to a board pack.
   ============================================================ */

import { getBrand, isDemonstration } from './brand.js';
import { getLogo } from './logo.js';

const PRINT_ROOT_ATTR = 'data-printing';

/**
 * Print a specific element, hiding everything else.
 * @param {string} targetId   element to print
 * @param {object} meta       { title, subtitle, footnote, preamble }
 *
 * `preamble` is an element inserted between the header and the content
 * — the front matter of a board pack. It is passed in rather than built
 * here because what belongs in front of a corridor run is not what
 * belongs in front of a scorecard, and this module has no business
 * knowing either. It is removed again on cleanup like everything else
 * injected for the print, so nothing survives into the live DOM.
 */
export async function printToPDF(targetId, meta = {}) {
  const target = document.getElementById(targetId);
  if (!target) {
    console.warn(`printToPDF: no element with id "${targetId}"`);
    return false;
  }

  const style = buildIsolationStyle(targetId);
  const header = buildPrintHeader(meta);
  const footnote = buildPrintFootnote(meta);
  const preamble = meta.preamble || null;

  document.head.appendChild(style);
  document.documentElement.setAttribute(PRINT_ROOT_ATTR, 'true');
  // Preamble first, then header prepended above it — so the order ends
  // up header, preamble, content whichever of the two exists.
  if (preamble) target.prepend(preamble);
  if (header) target.prepend(header);
  if (footnote) target.append(footnote);

  const cleanup = () => {
    style.remove();
    header?.remove();
    preamble?.remove();
    footnote?.remove();
    document.documentElement.removeAttribute(PRINT_ROOT_ATTR);
  };

  try {
    // Let layout settle before the synchronous print() call, otherwise
    // the injected header can be missing from the first page.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    window.print();
    return true;
  } catch (error) {
    console.error('Print failed:', error);
    return false;
  } finally {
    // afterprint is unreliable across browsers; a timeout guarantees cleanup.
    window.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(cleanup, 1500);
  }
}

function buildIsolationStyle(targetId) {
  const style = document.createElement('style');
  style.setAttribute('data-print-isolation', 'true');
  // Hide every sibling on the path from body to the target, so only the
  // target's subtree paints — without cloning it into a new document.
  style.textContent = `
    @media print {
      body * { visibility: hidden; }
      #${CSS.escape(targetId)}, #${CSS.escape(targetId)} * { visibility: visible; }
      #${CSS.escape(targetId)} {
        position: absolute; inset: 0 auto auto 0; width: 100%;
      }
      #${CSS.escape(targetId)} .no-print { display: none !important; }
    }`;
  return style;
}

/* The deliverable carries the operator's brand, not ours.

   It did not. A session under ?brand=kifwa changed the accent on screen
   and produced a byte-identical PDF to the default one: same header, a
   footnote reading "Produced with Kanda Logistics Advisory", no mark of
   the organisation whose name is on the page the operator was reading.

   The export is the one artefact that leaves the building. White-label
   that stops at the screen is white-label that stops exactly where it
   starts to matter. */
function buildPrintHeader({ title, subtitle }) {
  if (!title) return null;
  const brand = getBrand();

  const header = document.createElement('div');
  header.className = 'print-only print-header';

  /* The brand line above the title, because a reader who picks this up
     off a desk should know whose document it is before they know what
     it is about. */
  const mark = document.createElement('div');
  mark.className = 'print-brand';

  /* The operator's own upload outranks the skin's logo, and both
     outrank the name as text.

     That order is the point of the feature rather than a detail of it.
     A skin is the platform partner's identity; the upload is the
     identity of the business whose figures are on the page, and it is
     that business circulating the document. When an operator has gone
     to the trouble of putting their mark on their own reports, a
     partner skin must not paint over it. */
  const operatorLogo = getLogo();
  const logoSrc = operatorLogo?.dataUrl || brand.logo;

  if (logoSrc) {
    const img = document.createElement('img');
    img.src = logoSrc;
    /* The operator's own mark is not "KIFWA" just because the page is
       wearing that skin, and alt text that names the wrong organisation
       is worse than none. */
    img.alt = operatorLogo ? 'Operator logo' : brand.name;
    img.className = 'print-brand-logo';
    mark.appendChild(img);
  } else {
    const name = document.createElement('span');
    name.className = 'print-brand-name';
    name.textContent = brand.name;
    mark.appendChild(name);
  }
  header.appendChild(mark);

  const h1 = document.createElement('h1');
  h1.textContent = title;
  const meta = document.createElement('div');
  meta.className = 'meta';
  const generated = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(new Date());
  meta.textContent = subtitle ? `${subtitle} · Generated ${generated}` : `Generated ${generated}`;
  header.append(h1, meta);

  /* A demonstration skin has to say so on the document too. It already
     says so on screen; a PDF that drops the disclaimer is a PDF that can
     be forwarded as evidence of a partnership that does not exist. */
  if (isDemonstration()) {
    const demo = document.createElement('div');
    demo.className = 'print-demo-note';
    demo.textContent =
      `${brand.name} is shown here as a demonstration of a white-label deployment. ` +
      'It does not indicate an existing partnership.';
    header.appendChild(demo);
  }
  return header;
}

function buildPrintFootnote({ footnote }) {
  const node = document.createElement('div');
  node.className = 'print-only print-footnote';
  /* Composed, not hardcoded. This string named Kanda regardless of who
     the operator had branded the session as. */
  const brand = getBrand();
  const attribution = brand.poweredBy
    ? `Produced with ${brand.name}, powered by Kanda Logistics Advisory.`
    : `Produced with ${brand.name} ${brand.descriptor}.`;
  node.textContent =
    footnote ||
    `${attribution} All figures are computed on the user’s device from values they ` +
      'supplied; no data was transmitted. Benchmarks are indicative and carry the source and ' +
      'date shown against each figure. This document is decision support, not professional advice.';
  return node;
}

export function isPrintSupported() {
  return typeof window !== 'undefined' && typeof window.print === 'function';
}
